import { Box, Button, ButtonProps, Text } from "@chakra-ui/react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { authService } from "../../../config";
import { PreparedPasskey } from "../../../OryAuthService";

const PREPARED_REQUEST_REFRESH_MS = 10 * 60 * 1000;

type Props = Omit<ButtonProps, "onClick"> & {
  purpose: "Confirm" | "AddOrConfirm";
  label: string;
  onSuccess: () => void;
  onReauthRequired?: () => void;
};

export const PasskeyButton = ({ purpose, label, onSuccess, onReauthRequired, ...buttonProps }: Props) => {
  const { t } = useTranslation();
  const [prepared, setPrepared] = useState<PreparedPasskey>();
  const [preparation, setPreparation] = useState(0);
  const [isUsing, setIsUsing] = useState(false);
  const [problem, setProblem] = useState<"Cancelled" | "Failure" | "Unavailable">();

  useEffect(() => {
    let current = true;
    const prepare = purpose === "AddOrConfirm" ? authService.prepareAdminPasskey() : authService.prepareConfirmation();
    void prepare.then((next) => current && setPrepared(next));
    const refresh = setTimeout(() => setPreparation((count) => count + 1), PREPARED_REQUEST_REFRESH_MS);
    return () => {
      current = false;
      clearTimeout(refresh);
    };
  }, [purpose, preparation]);

  const prepareAgain = () => {
    setPrepared(undefined);
    setPreparation((count) => count + 1);
  };

  const onClick = async () => {
    if (!prepared) return;
    setProblem(undefined);
    switch (prepared.kind) {
      case "AlreadyConfirmed":
        prepareAgain();
        return onSuccess();
      case "Unavailable":
        setProblem("Unavailable");
        return prepareAgain();
    }
    setIsUsing(true);
    const result = await authService.completePasskey(prepared);
    setIsUsing(false);
    if (result === "Cancelled") return setProblem("Cancelled");
    prepareAgain();
    if (result === "Success") return onSuccess();
    if (result === "ReauthRequired" && onReauthRequired) return onReauthRequired();
    setProblem("Failure");
  };

  return (
    <Box>
      {problem && (
        <Text fontSize="sm" color="smError" mb="4">
          {problem === "Cancelled" && t("The passkey was not used. Try again when you are ready.")}
          {problem === "Failure" && t("We could not set up or confirm your passkey. Please try again.")}
          {problem === "Unavailable" && t("Passkeys are not available right now. Please contact us via chat.")}
        </Text>
      )}
      <Button {...buttonProps} isLoading={prepared === undefined || isUsing} onClick={() => void onClick()}>
        {label}
      </Button>
    </Box>
  );
};
